import {
  Autocomplete,
  Box,
  debounce,
  TextField,
  Typography,
} from "@mui/material";
import { FC, useEffect, useMemo, useState } from "react";
import { GooglePlacesAPI } from "../../../api";

interface SearchPlaceComponentProps {
  onSubmit?: (place: google.maps.places.Place | undefined) => void;
}

const SearchPlace: FC<SearchPlaceComponentProps> = ({ onSubmit }) => {
  const [input, setInput] = useState("");
  const [establishments, setEstablishments] = useState<
    Array<google.maps.places.AutocompleteSuggestion>
  >([]);
  const [selectedEstablishment, setSelectedEstablishment] =
    useState<google.maps.places.AutocompleteSuggestion | null>(null);

  const fetch = () => {
    if (input && input.length > 2) {
      GooglePlacesAPI.getAutocompleteSuggestionFromInput(input, [
        "establishment",
      ])
        .then((result) => {
          const filtered = result.filter(
            (suggestion) =>
              suggestion.placePrediction?.mainText?.text != undefined,
          );
          setEstablishments(filtered);
        })
        .catch((e) => {
          console.error("Error fetching organization! " + e);
        });
    } else setEstablishments([]);
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedFetch = useMemo(() => debounce(fetch, 200), [input]);
  useEffect(() => {
    debouncedFetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [input]);

  return (
    <Autocomplete
      freeSolo
      autoSelect
      value={selectedEstablishment}
      onChange={(_, value, reason) => {
        if (reason == "selectOption") {
          if (typeof value !== "string" && value != null) {
            setSelectedEstablishment(value);
            GooglePlacesAPI.getSuggestionAsPlace(value).then((place) => {
              if (onSubmit) onSubmit(place);
            });
          } else {
            setSelectedEstablishment(null);
            if (onSubmit) onSubmit(undefined);
          }
        }

        if (reason == "clear") {
          setSelectedEstablishment(null);
          if (onSubmit) onSubmit(undefined);
        }
      }}
      inputValue={input}
      onInputChange={(_, value) => {
        setInput(value);
        setSelectedEstablishment(null);
        if (onSubmit) onSubmit(undefined);
      }}
      options={establishments}
      renderInput={(params) => (
        <TextField
          {...params}
          color={
            selectedEstablishment || input.length < 2 ? "primary" : "error"
          }
          label="Organization"
        />
      )}
      getOptionLabel={(option) => {
        if (typeof option !== "string") {
          if (option.placePrediction && option.placePrediction.mainText)
            return `${option.placePrediction?.mainText?.text}`;
          else return "";
        } else {
          return option;
        }
      }}
      getOptionKey={(option) => {
        if (typeof option !== "string") {
          if (option.placePrediction && option.placePrediction.mainText)
            return `${option.placePrediction?.mainText?.text} ${option.placePrediction?.secondaryText?.text || ""}`;
          else return "";
        } else {
          return option;
        }
      }}
      renderOption={(props, option) => {
        if (typeof option === "string") return null;
        // eslint-disable-next-line react/prop-types
        const { key, ...other } = props;
        return (
          <Box
            component="li"
            {...other}
            key={key}
            sx={{ display: "flex", flexDirection: "row" }}
          >
            <Typography>{option.placePrediction?.mainText?.text}</Typography>
            <Typography sx={{ ml: 1, color: "#555555" }}>
              {`${option.placePrediction?.secondaryText?.text}`}
            </Typography>
          </Box>
        );
      }}
    />
  );
};

export default SearchPlace;
